import useGAnalytics, {
  EventAction,
  EventCategory,
} from "@/libs/hooks/tracking/useGAnalytics";
import useVisibilityTracker from "@/libs/hooks/tracking/useVisibilityTracking";
import { useCopies } from "@/libs/hooks/useCopy";
import { useSettings } from "@/libs/hooks/useSettings";
import { useMainDict } from "@/libs/hooks/useTranslation";
import { IconBrandInstagram } from "@tabler/icons-react";
import Image from "next/image";

export const FollowOnInstagramSection = () => {
  const { t } = useMainDict();

  const { trackEvent } = useGAnalytics();

  const { copy } = useCopies();
  const { settings } = useSettings();

  useVisibilityTracker("follow-on-insta-section", () => {
    trackEvent(
      "follow-on-insta-section-visible",
      EventCategory.USER_INTERACTION,
      EventAction.BECOMES_VISIBLE
    );
  });

  return (
    <section
      id="follow-on-insta-section"
      className="relative w-full overflow-hidden"
      aria-labelledby="follow-us-on-instagram"
    >
      {/* Vintage olive/sage background */}
      <div className="absolute inset-0 bg-gradient-to-br from-vintage-olive via-[#526645] to-[#485A3E]">
        {/* Pattern */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='1'/%3E%3Ccircle cx='13' cy='13' r='1'/%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        {/* Vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_50%,rgba(0,0,0,0.2)_100%)]" />
      </div>

      {/* Decorative top border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-vintage-cream/40 to-transparent" />

      <div className="relative z-10 flex lg:flex-row flex-col-reverse">
        {/* Image with vintage frame effect */}
        <div className="relative lg:w-1/2">
          {/* Decorative frame elements */}
          <div className="absolute top-4 left-4 w-16 h-16 border-l-2 border-t-2 border-vintage-cream/30 z-10" />
          <div className="absolute bottom-4 right-4 w-16 h-16 border-r-2 border-b-2 border-vintage-cream/30 z-10" />

          <Image
            src={"/DSC_9987.jpg"}
            width={800}
            height={800}
            alt={t("instagramImageAlt")}
            className="w-full h-full object-cover"
          />

          {/* Overlay gradient for text visibility on mobile */}
          <div className="absolute inset-0 bg-gradient-to-t from-vintage-olive/80 via-transparent to-transparent lg:hidden" />
        </div>

        {/* Content */}
        <div className="flex-1 flex items-center justify-center p-8 lg:p-16">
          <div className="text-center max-w-lg">
            {/* Decorative element */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-12 h-px bg-vintage-cream/40" />
              <IconBrandInstagram size={32} className="text-vintage-cream" />
              <div className="w-12 h-px bg-vintage-cream/40" />
            </div>

            <p
              id="follow-us-on-instagram"
              className="font-script text-4xl md:text-5xl lg:text-6xl text-vintage-cream leading-tight"
              style={{ textShadow: "2px 2px 4px rgba(0,0,0,0.3)" }}
            >
              {copy?.obserwujNasNaInstaCopy}
            </p>

            {/* Instagram handle */}
            <a
              href={settings?.instagramLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-3 mt-8 group"
              aria-label={t("followOnInstagramAriaLabel")}
            >
              <span className="font-display text-2xl md:text-3xl text-vintage-cream border-b-2 border-vintage-ochre pb-1 group-hover:border-vintage-cream transition-colors">
                @pizzicletta.krk
              </span>
            </a>

            {/* Decorative element */}
            <div className="flex items-center justify-center gap-4 mt-8">
              <div className="w-8 h-px bg-vintage-cream/40" />
              <span className="text-vintage-cream/60 text-lg">◆</span>
              <div className="w-8 h-px bg-vintage-cream/40" />
            </div>
          </div>
        </div>
      </div>

      {/* Decorative bottom border */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-vintage-cream/40 to-transparent" />
    </section>
  );
};
